import { useEffect, useState } from 'react';
import { dashboardAPI } from '../services/api';
import { StatusBadge, Loading } from '../components/ui';
import JobFlowModal from '../components/JobFlowModal';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  AreaChart, Area, PieChart, Pie, Legend, CartesianGrid, LineChart, Line,
} from 'recharts';

// ── Palette ──────────────────────────────────────────────────────────────────
const C = {
  navy:'#7C8CFF', blue:'#38BDF8', sky:'#7DD3FC', teal:'#34D399',
  yellow:'#FBBF24', orange:'#F472B6', red:'#F472B6', track:'#2A2356',
};
const SERIES = [C.blue, C.orange, C.teal, C.yellow, C.navy, C.sky];

const rs = (v) => `Rs. ${Number(v || 0).toLocaleString('en-LK')}`;
const compact = (v) => {
  const n = Number(v || 0);
  return n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k` : String(n);
};

// ── Card with tinted header bar ──────────────────────────────────────────────
const Panel = ({ title, right, children, className = '' }) => (
  <div className={`card overflow-hidden ${className}`}>
    <div className="flex items-center justify-between px-4 py-2.5 border-b border-ink-line bg-white/[.03]">
      <h3 className="text-[11px] font-semibold uppercase tracking-[.12em] text-ink-muted">{title}</h3>
      {right && <span className="text-[11px] text-ink-faint">{right}</span>}
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const Empty = ({ note, h = 190 }) => (
  <div style={{ height: h }} className="flex items-center justify-center text-sm text-gray-300">{note}</div>
);

// ── Solid colour KPI tile ────────────────────────────────────────────────────
const KpiTile = ({ icon, value, label, sub, bg }) => (
  <div className="card relative overflow-hidden px-4 pt-4 pb-5 transition-colors hover:border-white/20">
    <div className="flex items-start justify-between gap-3">
      <p className="text-[11px] font-medium uppercase tracking-[.14em] text-ink-muted">{label}</p>
      <span className="w-10 h-10 rounded-xl grid place-items-center text-lg flex-shrink-0 ring-1"
        style={{ background: `${bg}1F`, borderColor: 'transparent', boxShadow: `inset 0 0 0 1px ${bg}44` }}>{icon}</span>
    </div>
    <p className="mt-3 text-[2rem] leading-none font-display font-bold tracking-tight truncate"
       style={{ color: bg }}>{value}</p>
    {sub && <p className="mt-2 text-[11px] font-mono text-ink-faint truncate">{sub}</p>}
    {/* accent rail along the bottom edge */}
    <span className="absolute inset-x-0 bottom-0 h-[3px]"
      style={{ background: `linear-gradient(90deg, ${bg}, ${bg}00)` }} />
  </div>
);

// ── Progress bar vs target ───────────────────────────────────────────────────
const Progress = ({ pct, color, note }) => (
  <div className="flex items-center gap-3 mb-3">
    <div className="flex-1 h-3.5 rounded-full relative overflow-hidden" style={{ background: C.track }}>
      <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, pct)}%`, background: color, boxShadow: `0 0 14px -2px ${color}` }} />
      <span className="absolute top-0 bottom-0 w-[2px] bg-white/40" style={{ left: '75%' }} />
    </div>
    <span className="text-xs font-semibold text-gray-500 whitespace-nowrap">{note}</span>
  </div>
);

const StatRow = ({ label, value, color }) => (
  <div className="flex items-end justify-between py-2.5 border-b border-ink-line last:border-0">
    <span className="text-sm text-ink-muted">{label}</span>
    <span className="text-2xl font-display font-bold tracking-tight" style={{ color: color || C.navy }}>{value ?? '—'}</span>
  </div>
);

const Metric = ({ value, label, color }) => (
  <div className="text-center px-3 py-4 hover:bg-gray-50 transition-colors">
    <p className="text-2xl font-bold" style={{ color: color || C.navy }}>{value ?? '—'}</p>
    <p className="text-[11px] text-gray-500 leading-tight mt-1 uppercase tracking-wide">{label}</p>
  </div>
);

export default function Dashboard() {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [flowJob, setFlowJob] = useState(null);
  const [bucket,  setBucket]  = useState('repairs_awaiting_foreman');
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user?.role;

  // What each role sees
  const VIEW = {
    foreman:    { title:'FOREMAN DASHBOARD',   buckets:['repairs_awaiting_foreman','invoices_awaiting_foreman','accidents_open','repairs_rejected'], charts:['repairs','category','status'] },
    accountant: { title:'ACCOUNTS DASHBOARD',  buckets:['invoices_awaiting_payment','invoices_awaiting_foreman'], charts:['fuel','category'] },
    transport_officer: { title:'TRANSPORT OFFICER DASHBOARD', buckets:['repairs_rejected','jobs_awaiting_invoice','repairs_awaiting_foreman','invoices_awaiting_foreman','accidents_open'], charts:['fuel','repairs','compliance','status','category','topfuel'] },
    manager:    { title:'FLEET OVERVIEW',      buckets:'all', charts:'all' },
    admin:      { title:'FLEET ANALYSIS',      buckets:'all', charts:'all' },
    driver:     { title:'MY DASHBOARD',        buckets:[], charts:[] },
    mechanic:   { title:'WORKSHOP DASHBOARD',  buckets:['repairs_awaiting_foreman','jobs_awaiting_invoice'], charts:['repairs','category'] },
  }[role] || { title:'DASHBOARD', buckets:'all', charts:'all' };

  const showChart  = (k) => VIEW.charts === 'all' || VIEW.charts.includes(k);
  const isPersonal = role === 'driver';

  useEffect(() => {
    dashboardAPI.getSummary()
      .then(r => setData(r.data.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading />;
  if (!data)   return <p className="text-gray-500">Failed to load dashboard.</p>;

  const { vehicles, repairs, drivers, fuel, compliance, inventory, recent_repairs, license_expiry,
          fuel_trend = [], repair_trend = [], repair_by_type = [], top_fuel = [], pending_work = {},
          my_work = { items: [] } } = data;

  const BUCKETS = [
    { key:'repairs_awaiting_foreman',  label:'Repairs — foreman review', color:'#FBB03B', kind:'repair' },
    { key:'repairs_rejected',          label:'Rejected — officer action', color:'#F0246E', kind:'repair' },
    { key:'jobs_awaiting_invoice',     label:'Jobs — awaiting invoice',   color:'#00A6C8', kind:'repair' },
    { key:'invoices_awaiting_foreman', label:'Invoices — foreman review', color:'#1B3A93', kind:'invoice' },
    { key:'invoices_awaiting_payment', label:'Invoices — accounts payment', color:'#0FC08A', kind:'invoice' },
    { key:'accidents_open',            label:'Accident claims — open',    color:'#F0246E', kind:'accident' },
  ];
  const visibleBuckets = VIEW.buckets === 'all' ? BUCKETS : BUCKETS.filter(b => VIEW.buckets.includes(b.key));
  const totalPending = visibleBuckets.reduce((t, b) => t + ((pending_work[b.key] || []).length), 0);
  const activeBucket = visibleBuckets.find(b => b.key === bucket) || visibleBuckets[0];
  const rows = activeBucket ? (pending_work[activeBucket.key] || []) : [];

  const totalVehicles = +vehicles?.total || 0;
  const fuelSpark   = fuel_trend.map(f => ({ v: +f.cost || 0 }));
  const repairSpark = repair_trend.map(r => ({ v: +r.total || 0 }));

  const outOfService = (+vehicles?.under_repair || 0) + (+vehicles?.inactive || 0);
  const availPct     = totalVehicles ? Math.round(((+vehicles?.active || 0) / totalVehicles) * 100) : 0;
  const repairTotal  = repair_by_type.reduce((t, r) => t + (+r.count || 0), 0);
  const repairDonePct= repairTotal ? Math.round(((repairTotal - (+repairs?.pending || 0)) / repairTotal) * 100) : 0;
  const docIssues    = (+compliance?.expired || 0) + (+compliance?.expiring_soon || 0);
  const compliantPct = totalVehicles ? Math.max(0, Math.round(((totalVehicles - docIssues) / totalVehicles) * 100)) : 0;

  const statusData = [
    { name: 'Active',       value: +vehicles?.active       || 0, fill: C.navy },
    { name: 'Assigned',     value: +vehicles?.assigned     || 0, fill: C.blue },
    { name: 'Under Repair', value: +vehicles?.under_repair || 0, fill: C.orange },
    { name: 'Inactive',     value: +vehicles?.inactive     || 0, fill: C.sky },
  ];
  const complianceData = [
    { name: 'Valid',    value: Math.max(totalVehicles - (+compliance?.expired || 0) - (+compliance?.expiring_soon || 0), 0), fill: C.navy },
    { name: 'Expiring', value: +compliance?.expiring_soon || 0, fill: C.yellow },
    { name: 'Expired',  value: +compliance?.expired || 0, fill: C.orange },
  ].filter(d => d.value > 0);
  const complianceTotal = complianceData.reduce((t, d) => t + d.value, 0);

  const tooltipStyle = { borderRadius: 8, border: '1px solid #3E3573', background: '#201A46', color: '#F4F1FC', fontSize: 12 };

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between">
        <div>
          <h2 className="text-2xl font-extrabold tracking-tight" style={{ color: C.navy }}>{VIEW.title}</h2>
          <p className="text-xs text-gray-400 mt-0.5">Welcome back, {user?.name} · <span className="capitalize">{(role || '').replace('_',' ')}</span></p>
        </div>
        <div className="flex gap-2 text-xs">
          {[['Vehicles', C.blue], ['Repairs', C.orange], ['Fuel', C.yellow], ['Compliance', C.teal]].map(([l, c]) => (
            <span key={l} className="px-2.5 py-1 rounded-md font-medium" style={{ background: '#EAF1FE', color: c }}>{l}</span>
          ))}
        </div>
      </div>

      {/* ── My work (role-specific) ───────────────────────────────────────── */}
      {my_work.items?.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
          <div className="px-4 py-2.5" style={{ background: '#EAF1FE' }}>
            <h3 className="text-sm font-semibold" style={{ color: C.navy }}>NEEDS YOUR ATTENTION</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 p-4">
            {my_work.items.map(it => (
              <button key={it.key} onClick={() => navigate(it.link)}
                className="flex items-center justify-between gap-3 rounded-lg border border-gray-100 px-4 py-3 text-left
                           hover:shadow-sm hover:-translate-y-0.5 transition-all"
                style={{ borderLeft: `4px solid ${it.color}` }}>
                <span className="text-sm text-gray-600">{it.label}</span>
                <span className="text-2xl font-bold whitespace-nowrap" style={{ color: it.count ? it.color : '#C7CDD9' }}>
                  {it.isMoney ? rs(it.count) : it.count}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Driver / mechanic personal lists ──────────────────────────────── */}
      {isPersonal && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <Panel title="MY VEHICLES" right="currently assigned">
            {my_work.my_vehicles?.length ? (
              <div className="divide-y divide-gray-50 -mx-4">
                {my_work.my_vehicles.map(v => (
                  <div key={v.id} className="px-4 py-3">
                    <p className="text-sm font-medium text-gray-800">{v.reg_number} — {v.brand} {v.model}</p>
                    <p className="text-xs text-gray-400">Since {v.start_date}{v.purpose ? ` · ${v.purpose}` : ''}</p>
                  </div>
                ))}
              </div>
            ) : <Empty note="No vehicle assigned to you" h={120} />}
          </Panel>
          <Panel title="MY REPAIR REQUESTS" right="open items">
            {my_work.my_repairs?.length ? (
              <div className="divide-y divide-gray-50 -mx-4">
                {my_work.my_repairs.map(r => (
                  <button key={r.id} onClick={() => setFlowJob(r.id)}
                    className="w-full text-left px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors">
                    <div>
                      <p className="text-sm font-medium text-gray-800">{r.title}</p>
                      <p className="text-xs text-gray-400">{r.reg_number} · {r.requested_date}</p>
                    </div>
                    <StatusBadge status={r.status} />
                  </button>
                ))}
              </div>
            ) : <Empty note="No open requests" h={120} />}
          </Panel>
        </div>
      )}

      {/* ── KPI tiles ─────────────────────────────────────────────────────── */}
      {!isPersonal && (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiTile icon="🚚" value={totalVehicles}                     label="Total Vehicles"  sub={`${vehicles?.active || 0} active`}    bg="#00A6C8" />
        <KpiTile icon="🔧" value={+repairs?.pending || 0}            label="Pending Repairs" sub={`${repairs?.critical || 0} critical`} bg="#F0246E" />
        <KpiTile icon="⛽" value={rs(fuel?.this_month_cost)}         label="Fuel This Month" sub={`${Number(fuel?.this_month_litres || 0).toFixed(0)} L`} bg="#0FC08A" />
        <KpiTile icon="⚠️" value={(+compliance?.expired || 0) + (+compliance?.expiring_soon || 0)}
                 label="Compliance Issues" sub={`${compliance?.expired || 0} expired`} bg="#FBB03B" />
      </div>)}

      {/* ── Availability / workload / documents ───────────────────────────── */}
      {!isPersonal && (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <Panel title="FLEET AVAILABILITY" right="vs total fleet">
          <Progress pct={availPct} color="#00A6C8" note={`${availPct}%`} />
          <StatRow label="Available now"  value={+vehicles?.active || 0} />
          <StatRow label="On assignment"  value={+vehicles?.assigned || 0} />
          <StatRow label="Out of service" value={(+vehicles?.under_repair || 0) + (+vehicles?.inactive || 0)} color="#F0246E" />
        </Panel>

        <Panel title="REPAIR WORKLOAD" right="all requests">
          <Progress pct={repairDonePct} color="#0FC08A" note={`${repairDonePct}% done`} />
          <StatRow label="Pending approval" value={+repairs?.pending || 0} color="#FBB03B" />
          <StatRow label="Critical jobs"    value={+repairs?.critical || 0} color="#F0246E" />
          <StatRow label="Under repair"     value={+vehicles?.under_repair || 0} />
        </Panel>

        <Panel title="DOCUMENTS & STOCK" right="attention needed">
          <Progress pct={compliantPct} color="#FBB03B" note={`${compliantPct}% valid`} />
          <StatRow label="Expired documents" value={+compliance?.expired || 0} color="#F0246E" />
          <StatRow label="Expiring soon"     value={+compliance?.expiring_soon || 0} color="#FBB03B" />
          <StatRow label="Low stock items"   value={+inventory?.low_stock || 0} />
        </Panel>
      </div>)}

      {/* ── Pending work ──────────────────────────────────────────────────── */}
      {visibleBuckets.length > 0 && (
      <Panel title="PENDING WORK" right={`${totalPending} item${totalPending === 1 ? '' : 's'} not complete`}>
        {/* Workflow stages — each card is still the filter control for the list below */}
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 mb-5">
          {visibleBuckets.map(b => {
            const n = (pending_work[b.key] || []).length;
            const on = bucket === b.key;
            return (
              <button key={b.key} onClick={() => setBucket(b.key)}
                aria-pressed={on}
                title={b.label}
                className={`relative overflow-hidden rounded-xl px-3 pt-3 pb-4 text-left transition-all
                  ${on ? 'ring-1 bg-white/[.06]' : 'ring-1 ring-ink-line bg-white/[.02] hover:bg-white/[.05]'}`}
                style={on ? { boxShadow: `inset 0 0 0 1px ${b.color}88, 0 0 22px -10px ${b.color}` } : undefined}>
                <span className="block w-7 h-7 rounded-lg mb-2 grid place-items-center text-xs"
                  style={{ background: `${b.color}22`, boxShadow: `inset 0 0 0 1px ${b.color}55` }}>
                  {b.kind === 'repair' ? '🔧' : b.kind === 'invoice' ? '🧾' : '💥'}
                </span>
                <span className="block text-[2rem] leading-none font-display font-bold tracking-tight"
                  style={{ color: n ? b.color : 'var(--ink-faint)' }}>{n}</span>
                <span className="block mt-1.5 text-[10px] leading-snug uppercase tracking-wide text-ink-muted line-clamp-2">
                  {b.label}
                </span>
                <span className="absolute inset-x-0 bottom-0 h-[3px]"
                  style={{ background: n ? `linear-gradient(90deg, ${b.color}, ${b.color}00)` : 'transparent' }} />
              </button>
            );
          })}
        </div>

        {rows.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-300">Nothing pending here ✓</p>
        ) : (
          <div className="divide-y divide-gray-50 -mx-4">
            {rows.map(r => {
              const isRepair = activeBucket.kind === 'repair';
              const jobId = isRepair ? r.id : r.repair_id;
              return (
                <button key={`${activeBucket.key}-${r.id}`}
                  onClick={() => {
                    if (activeBucket.kind === 'accident') return navigate('/accidents');
                    if (jobId) setFlowJob(jobId);
                    else navigate('/invoices');
                  }}
                  className="w-full text-left px-4 py-3 flex items-center justify-between gap-3 hover:bg-gray-50 transition-colors">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">
                      <span className="font-mono text-xs mr-2 px-1.5 py-0.5 rounded"
                        style={{ background: '#EAF1FE', color: '#1B3A93' }}>
                        {activeBucket.kind === 'accident'
                          ? `ACC-${String(r.id).padStart(4,'0')}`
                          : `JOB-${String(jobId || r.id).padStart(4,'0')}`}
                      </span>
                      {r.title || r.invoice_no || r.place || 'Accident report'}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {r.reg_number || '—'}
                      {r.requester ? ` · ${r.requester}` : r.submitter ? ` · ${r.submitter}` : r.reporter ? ` · ${r.reporter}` : ''}
                      {r.requested_date ? ` · ${r.requested_date}` : r.accident_date ? ` · ${r.accident_date}` : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {(r.total_amount || r.amount || r.estimated_cost || r.quoted_amount) && (
                      <span className="text-xs font-semibold text-gray-600">
                        {rs(r.total_amount || r.amount || r.estimated_cost || r.quoted_amount)}
                      </span>
                    )}
                    {r.urgency && <StatusBadge status={r.urgency} />}
                    <span className="text-gray-300 text-xs">›</span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
        <p className="text-[11px] text-gray-400 mt-3">Click any row to see its current status and full workflow.</p>
      </Panel>)}

      {/* ── Row 1 ─────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {showChart('fuel') && <Panel title="Fuel Cost YoY" right="Last 6 months">
          {fuel_trend.length ? (
            <ResponsiveContainer width="100%" height={190}>
              <AreaChart data={fuel_trend} margin={{ top: 5, right: 5, left: -18, bottom: 0 }}>
                <defs>
                  <linearGradient id="gFuel" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor={C.blue} stopOpacity={0.4} />
                    <stop offset="95%" stopColor={C.blue} stopOpacity={0.03} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#3E3573" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#B7ADD8' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#B7ADD8' }} axisLine={false} tickLine={false} tickFormatter={compact} />
                <Tooltip formatter={(v) => rs(v)} contentStyle={tooltipStyle} />
                <Area type="monotone" dataKey="cost" stroke={C.navy} strokeWidth={2.5} fill="url(#gFuel)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : <Empty note="No fuel logs yet" />}
        </Panel>}

        {showChart('repairs') && <Panel title="Repairs YoY" right="Raised vs completed">
          {repair_trend.length ? (
            <ResponsiveContainer width="100%" height={190}>
              <BarChart data={repair_trend} barGap={3} margin={{ top: 5, right: 5, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#3E3573" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#B7ADD8' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#B7ADD8' }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip cursor={{ fill: 'rgba(139,92,246,.07)' }} contentStyle={tooltipStyle} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="total"     name="Raised"    fill={C.navy}   radius={[3,3,0,0]} />
                <Bar dataKey="completed" name="Completed" fill={C.yellow} radius={[3,3,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <Empty note="No repair requests yet" />}
        </Panel>}

        {showChart('compliance') && <Panel title="Compliance" right={`${totalVehicles} vehicles`}>
          {complianceData.length ? (
            <ResponsiveContainer width="100%" height={190}>
              <PieChart>
                <Pie data={complianceData} dataKey="value" nameKey="name" innerRadius={48} outerRadius={78}
                  paddingAngle={2} stroke="none"
                  label={({ value }) => `${Math.round((value / complianceTotal) * 100)}%`}
                  labelLine={false} style={{ fontSize: 11, fontWeight: 600 }}>
                  {complianceData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                </Pie>
                <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                <Tooltip contentStyle={tooltipStyle} />
              </PieChart>
            </ResponsiveContainer>
          ) : <Empty note="No compliance data" />}
        </Panel>}
      </div>

      {/* ── Row 2 ─────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {showChart('status') && <Panel title="Vehicle Status">
          <ResponsiveContainer width="100%" height={190}>
            <BarChart data={statusData} barSize={30} margin={{ top: 5, right: 5, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#3E3573" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#B7ADD8' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#B7ADD8' }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip cursor={{ fill: 'rgba(139,92,246,.07)' }} contentStyle={tooltipStyle} />
              <Bar dataKey="value" radius={[4,4,0,0]}>
                {statusData.map((d, i) => <Cell key={i} fill={d.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>}

        {showChart('category') && <Panel title="Repairs by Category">
          {repair_by_type.length ? (
            <ResponsiveContainer width="100%" height={190}>
              <PieChart>
                <Pie data={repair_by_type} dataKey="count" nameKey="name" innerRadius={0} outerRadius={78}
                  stroke="none" label={({ name }) => name} labelLine={false} style={{ fontSize: 10 }}>
                  {repair_by_type.map((d, i) => <Cell key={i} fill={SERIES[i % SERIES.length]} />)}
                </Pie>
                <Tooltip formatter={(v, n, p) => [`${v} jobs · ${rs(p.payload.cost)}`, p.payload.name]}
                  contentStyle={tooltipStyle} />
              </PieChart>
            </ResponsiveContainer>
          ) : <Empty note="No repair data" />}
        </Panel>}

        {showChart('topfuel') && <Panel title="Top Fuel Consumers" right="This month">
          {top_fuel.length ? (
            <ResponsiveContainer width="100%" height={190}>
              <BarChart data={top_fuel} layout="vertical" barSize={16} margin={{ top: 5, right: 15, left: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#3E3573" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: '#B7ADD8' }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: '#B7ADD8' }} width={78} axisLine={false} tickLine={false} />
                <Tooltip formatter={(v, n, p) => [`${Number(v).toFixed(1)} L · ${rs(p.payload.cost)}`, 'Fuel']}
                  cursor={{ fill: 'rgba(139,92,246,.07)' }} contentStyle={tooltipStyle} />
                <Bar dataKey="litres" radius={[0,4,4,0]}>
                  {top_fuel.map((d, i) => <Cell key={i} fill={SERIES[i % SERIES.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <Empty note="No fuel logs this month" />}
        </Panel>}
      </div>

      {/* ── Metric strip ──────────────────────────────────────────────────── */}
      {!isPersonal && (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-4 py-2.5" style={{ background: '#EAF1FE' }}>
          <h3 className="text-sm font-semibold" style={{ color: C.navy }}>Fleet Summary</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 divide-x divide-y lg:divide-y-0 divide-gray-100">
          <Metric value={totalVehicles}                  label="Total Vehicles" color="#00A6C8" />
          <Metric value={vehicles?.under_repair}         label="Under Repair"   color="#F0246E" />
          <Metric value={vehicles?.assigned}             label="Assigned"       color="#1B3A93" />
          <Metric value={drivers?.active}                label="Active Drivers" color="#0FC08A" />
          <Metric value={`${Number(fuel?.this_month_litres || 0).toFixed(0)} L`} label="Litres This Month" color="#FBB03B" />
          <Metric value={inventory?.low_stock}           label="Low Stock"      color="#F0246E" />
        </div>
      </div>)}

      {/* ── Lists ─────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Panel title="Recent Repair Requests" className="!p-0">
          <div className="-m-4 divide-y divide-gray-50">
            {recent_repairs?.length ? recent_repairs.map(r => (
              <div key={r.id} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors">
                <div>
                  <p className="text-sm font-medium text-gray-800 truncate max-w-[200px]">{r.title}</p>
                  <p className="text-xs text-gray-400">{r.reg_number} · {r.requester}</p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <StatusBadge status={r.status} />
                  <StatusBadge status={r.urgency} />
                </div>
              </div>
            )) : <p className="px-4 py-8 text-sm text-gray-300 text-center">No recent repairs</p>}
          </div>
        </Panel>

        <Panel title="Driver Licence Expiring" right="Next 60 days">
          <div className="-m-4 divide-y divide-gray-50">
            {license_expiry?.length ? license_expiry.map((d, i) => (
              <div key={i} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors">
                <div>
                  <p className="text-sm font-medium text-gray-800">{d.name}</p>
                  <p className="text-xs text-gray-400">{d.phone}</p>
                </div>
                <span className="text-xs font-semibold px-2.5 py-1 rounded-md"
                  style={{ background: '#FEF3C7', color: '#92400E' }}>{d.license_expiry}</span>
              </div>
            )) : <p className="px-4 py-8 text-sm text-gray-300 text-center">All licences valid ✓</p>}
          </div>
        </Panel>
      </div>
      <JobFlowModal repairId={flowJob} open={!!flowJob} onClose={() => setFlowJob(null)} />
    </div>
  );
}
